import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Download as DownloadIcon,
    Shield,
    Smartphone,
    CheckCircle2,
    ArrowRight,
    Sparkles,
    Apple,
    Bell,
    Loader2,
    Mail,
} from "lucide-react";
import FemcareLogo from "../components/FemcareLogo";
import { api } from "../lib/api";
import PageHero from "../components/PageHero";
import { useReveal } from "../lib/motion";

export default function Download() {
    useReveal();
    const [started, setStarted] = useState(false);
    const [iosEmail, setIosEmail] = useState("");
    const [iosSubmitting, setIosSubmitting] = useState(false);
    const [iosDone, setIosDone] = useState(false);
    const [iosError, setIosError] = useState("");
    const [apkInfo, setApkInfo] = useState(null);

    // Fetch current build metadata (size, upload date). The button + subtitle
    // always reflect whatever the admin most recently uploaded — no hardcoded
    // version strings that go stale.
    useEffect(() => {
        api.get("/downloads/apk/info")
            .then((r) => setApkInfo(r.data))
            .catch(() => setApkInfo({ available: false }));
    }, []);

    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const downloadUrl = `${backendUrl}/api/downloads/apk`;
    const sizeMB = apkInfo?.size_bytes ? Math.round(apkInfo.size_bytes / (1024 * 1024)) : null;
    const uploadedLabel = apkInfo?.uploaded_at
        ? new Date(apkInfo.uploaded_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
        : null;

    const triggerAndroid = () => {
        if (!apkInfo?.available) return;
        setStarted(true);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = "femcare-latest.apk";
        a.rel = "noopener";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const submitIosNotify = async (e) => {
        e.preventDefault();
        if (iosSubmitting) return;
        setIosSubmitting(true);
        setIosError("");
        try {
            // Reuses the newsletter /subscribe endpoint with source="ios-waitlist"
            // so these emails show up as a segment in the admin dashboard.
            await api.post("/subscribe", {
                email: iosEmail.trim().toLowerCase(),
                source: "ios-waitlist",
            });
            setIosDone(true);
        } catch (err) {
            const d = err.response?.data?.detail;
            setIosError(typeof d === "string" ? d : "Could not save your email. Please try again.");
        } finally {
            setIosSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-femcare-cream" data-testid="download-page">
            <PageHero
                testId="download-hero"
                overline="Get the app"
                title={<>Femcare,<br /><span className="italic font-light">in your pocket.</span></>}
                subtitle="The private women's-health companion — cycle tracking, fertility, pregnancy mode, AI Care Assistant, and Daughter Care."
                size="md"
            />

            <div className="max-w-4xl mx-auto px-6 pb-16">

                {/* Two download tiles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8" data-testid="download-options">
                    {/* ANDROID */}
                    <div className="bg-white border border-femcare-border rounded-3xl p-7 shadow-lg shadow-femcare-pink/5 flex flex-col" data-testid="android-tile">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-femcare-pink to-femcare-accent text-white flex items-center justify-center">
                                <Smartphone className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-[0.2em] text-femcare-accent">For Android</p>
                                <h2 className="font-serif text-2xl text-femcare-purple">Download APK</h2>
                            </div>
                        </div>
                        <p className="text-sm text-femcare-purple/70 mb-5 leading-relaxed flex-1">
                            Direct install from a signed APK. Works on Android 8.0 &amp; above. No Play Store required.
                        </p>
                        <button
                            onClick={triggerAndroid}
                            disabled={!apkInfo || !apkInfo.available}
                            data-testid="download-android-btn"
                            className="w-full btn-primary justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <DownloadIcon className="w-4 h-4" />
                            {!apkInfo
                                ? "Loading…"
                                : !apkInfo.available
                                    ? "No build available yet"
                                    : started
                                        ? "Download started"
                                        : "Download latest build"}
                        </button>
                        <p className="mt-2 text-[11px] text-femcare-purple/50 text-center">
                            {apkInfo?.available ? "Verified latest build" : "Android 8.0+"}
                        </p>
                    </div>

                    {/* iOS — coming soon with email capture */}
                    <div className="bg-white border border-femcare-border rounded-3xl p-7 shadow-lg shadow-femcare-purple/5 flex flex-col relative overflow-hidden" data-testid="ios-tile">
                        <span className="absolute top-3 right-3 text-[10px] uppercase tracking-wider bg-femcare-petal text-femcare-accent px-2.5 py-1 rounded-full font-medium">
                            Coming soon
                        </span>
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-11 h-11 rounded-2xl bg-femcare-purple text-white flex items-center justify-center">
                                <Apple className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-[0.2em] text-femcare-accent">For iOS</p>
                                <h2 className="font-serif text-2xl text-femcare-purple">iPhone &amp; iPad</h2>
                            </div>
                        </div>

                        {!iosDone ? (
                            <>
                                <p className="text-sm text-femcare-purple/70 mb-5 leading-relaxed flex-1">
                                    Femcare for iOS is on its way. Drop your email and we'll let you know
                                    the moment it lands on the App Store.
                                </p>
                                <form onSubmit={submitIosNotify} className="space-y-2" data-testid="ios-notify-form">
                                    <div className="relative">
                                        <Mail className="w-4 h-4 text-femcare-purple/40 absolute left-3 top-1/2 -translate-y-1/2" />
                                        <input
                                            type="email"
                                            required
                                            value={iosEmail}
                                            onChange={(e) => setIosEmail(e.target.value)}
                                            placeholder="you@example.com"
                                            disabled={iosSubmitting}
                                            data-testid="ios-notify-email"
                                            className="w-full bg-femcare-cream border border-femcare-border rounded-xl pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-femcare-pink disabled:opacity-60"
                                        />
                                    </div>
                                    {iosError && <p className="text-xs text-red-600">{iosError}</p>}
                                    <button
                                        type="submit"
                                        disabled={iosSubmitting || !iosEmail}
                                        data-testid="ios-notify-submit"
                                        className="w-full btn-secondary justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {iosSubmitting ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                                            </>
                                        ) : (
                                            <>
                                                <Bell className="w-4 h-4" /> Notify me
                                            </>
                                        )}
                                    </button>
                                </form>
                                <p className="mt-2 text-[11px] text-femcare-purple/50 text-center">
                                    One email when we launch. No spam.
                                </p>
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center flex-1 text-center py-6" data-testid="ios-notify-done">
                                <div className="w-12 h-12 rounded-full bg-emerald-50 border-2 border-emerald-500 flex items-center justify-center mb-3">
                                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                                </div>
                                <p className="font-serif text-xl text-femcare-purple mb-1">You're on the list</p>
                                <p className="text-sm text-femcare-purple/70">
                                    We'll email <b className="text-femcare-purple">{iosEmail}</b> the day Femcare hits the App Store.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Install steps (Android only) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="install-steps">
                    {[
                        { icon: DownloadIcon, title: "Download", body: "Tap Download APK. Your browser saves the file to your phone's Downloads folder." },
                        { icon: Shield,       title: "Allow install", body: "In Settings, allow your browser (Chrome/Samsung Internet) to install apps from this source." },
                        { icon: Smartphone,   title: "Open Femcare", body: "Tap the downloaded APK, install, then open Femcare and sign in with email or Google." },
                    ].map(({ icon: Icon, title, body }, i) => (
                        <div key={i} className="bg-white border border-femcare-border rounded-2xl p-4">
                            <div className="w-9 h-9 rounded-full bg-femcare-petal/50 flex items-center justify-center mb-2.5">
                                <Icon className="w-4 h-4 text-femcare-pink" />
                            </div>
                            <p className="text-[10px] uppercase tracking-wider text-femcare-accent font-medium mb-0.5">Step {i + 1}</p>
                            <h3 className="font-serif text-base text-femcare-purple mb-0.5">{title}</h3>
                            <p className="text-xs text-femcare-purple/65 leading-relaxed">{body}</p>
                        </div>
                    ))}
                </div>

                {/* Web app fallback */}
                <div className="flex items-start justify-between gap-3 bg-femcare-petal/30 border border-femcare-border rounded-2xl p-4">
                    <div>
                        <p className="font-serif text-femcare-purple">Prefer the web experience?</p>
                        <p className="text-sm text-femcare-purple/65 mt-0.5">
                            Use Femcare in your browser while you wait — same tracking, same insights.
                        </p>
                    </div>
                    <Link
                        to="/"
                        className="hidden sm:inline-flex items-center gap-1.5 text-sm text-femcare-pink hover:text-femcare-rose transition font-medium whitespace-nowrap"
                    >
                        Open web app <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>

                <p className="mt-8 text-xs text-femcare-purple/50 text-center">
                    Install issues? Email <a href="mailto:support@femcare.life" className="text-femcare-pink underline">support@femcare.life</a> ·
                    <Link to="/support#troubleshooting" className="text-femcare-pink underline ml-1">Troubleshooting</Link>
                </p>
            </div>
        </div>
    );
}
